import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { JonumVerificationStatus } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconAddressCard } from '#src/atoms/icons/system/iconAddressCard.ts';
import { iconLock } from '#src/atoms/icons/system/iconLock.ts';
import { iconUser } from '#src/atoms/icons/system/iconUser.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BodyS, HeadlineL } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useApplyJonumRestriction } from '#src/hooks/useApplyJonumRestriction.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import { JonumKycPromptDialog } from '../JonumVerificationDialog/JonumKycPromptDialog.tsx';

const StyledDialog = styled(Dialog)`
  @media ${laptopAndAbove} {
    max-height: 80vh;
  }
`;

const Root = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--c-black);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 50% 35%,
      rgba(var(--c-rgb-red-400), 0.25),
      transparent 70%
    );
    pointer-events: none;
  }
`;

const Content = styled(Vertical)`
  position: relative;
  z-index: 1;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: var(--quadruple-unit) var(--double-unit);
  gap: var(--quadruple-unit);
`;

const Avatar = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: rgba(var(--c-rgb-white), 0.07);
  color: var(--c-nd-500);
  font-size: 48px;
  flex-shrink: 0;
`;

const LockBadge = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--c-red-400);
  color: var(--c-white);
  font-size: 16px;
  box-shadow: 0 0 0 var(--half-unit) var(--c-black);
`;

const TextContent = styled(Vertical)`
  width: 100%;
  align-items: center;
  text-align: center;
  gap: var(--unit);
`;

const Title = styled(HeadlineL)`
  color: var(--c-white);
  width: 100%;
`;

const Description = styled(BodyM)`
  color: var(--c-nd-600);
  width: 100%;
`;

const InfoRow = styled(Horizontal).attrs({ gap: 1.5 })`
  width: 100%;
  align-items: center;
`;

const InfoIcon = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(var(--c-rgb-white), 0.07);
  color: var(--c-white);
  flex-shrink: 0;
`;

const Actions = styled(Vertical)`
  position: relative;
  z-index: 1;
  margin-top: var(--double-unit);
  padding: 0 var(--double-unit) var(--double-unit);
  width: 100%;
`;

export const JonumAccountLockedDialog = () => {
  const {
    flags: { jonumUserInfoCollection = false },
  } = useFeatureFlags();
  const isJonumActive = useApplyJonumRestriction();
  const { currentUser } = useCurrentUserContext();
  const [kycOpen, setKycOpen] = useState(false);

  const status = currentUser?.jonumVerification?.status;
  const shouldShow =
    !!currentUser &&
    jonumUserInfoCollection &&
    isJonumActive &&
    status === JonumVerificationStatus.ACCOUNT_FROZEN;

  // Keep the dialog mounted once shown so it can animate closed when the
  // account is no longer frozen; only unmount after the close transition.
  const [mounted, setMounted] = useState(shouldShow);
  if (shouldShow && !mounted) setMounted(true);

  if (!mounted) return null;

  return (
    <>
      <StyledDialog
        open={shouldShow}
        onClosed={() => setMounted(false)}
        hideHeader
        maxWidth="xs"
        fullWidth
        disableBackdropClick
        disableEscapeKeyDown
      >
        <Root>
          <Content>
            <Avatar>
              <FontAwesomeIcon icon={iconUser} />
              <LockBadge>
                <FontAwesomeIcon icon={iconLock} />
              </LockBadge>
            </Avatar>
            <TextContent>
              <Title bold>
                <FormattedMessage defaultMessage="Account locked" />
              </Title>
              <Description>
                <FormattedMessage defaultMessage="Your account is currently restricted because your identity has not been verified." />
              </Description>
            </TextContent>
            <InfoRow>
              <InfoIcon>
                <FontAwesomeIcon icon={iconAddressCard} />
              </InfoIcon>
              <BodyS color="var(--c-nd-600)">
                <FormattedMessage defaultMessage="You can no longer play on Sorare until verification is complete." />
              </BodyS>
            </InfoRow>
          </Content>
          <Actions>
            <Button
              size="medium"
              color="primary"
              fullWidth
              onClick={() => setKycOpen(true)}
            >
              <FormattedMessage defaultMessage="Verify my identity" />
            </Button>
          </Actions>
        </Root>
      </StyledDialog>
      <JonumKycPromptDialog open={kycOpen} onDone={() => setKycOpen(false)} />
    </>
  );
};
